/**
 * Effective availability of a session for a specific buyer during checkout
 * preselect — mirrors DateSelectionStep's `effectiveAvailabilityFor`:
 * `max(0, availability - reservedSessionQuantities[id])`.
 *
 * `reservedSessionQuantities[id]` is the number of seats this buyer has already
 * consumed for the session in their in-progress basket, so it is SUBTRACTED from
 * the (server, hold-aware) availability — the gate and the date card must agree.
 * At preselect time (a fresh "Book Now" deep link) the basket is normally empty,
 * so this reduces to the session's availability.
 *
 * A queue offer-hold subtracts from `availability` server-side, so a fully-held
 * session reads 0 here for a non-claimer (claimers pass `?offer=`, which keeps
 * their own held seats visible).
 *
 * Used to decide whether a deep-linked ("Book Now") preselected session may
 * auto-advance to the tickets step, or must land on the dates step where the
 * card shows sold out / "Join Waiting List".
 */
export function effectivePreselectAvailability(
  session: { id?: string; availability?: number } | null | undefined,
  reservedSessionQuantities?: Record<string, number>,
): number {
  if (!session) return 0;
  const reserved = session.id
    ? (reservedSessionQuantities?.[session.id] ?? 0)
    : 0;
  return Math.max(0, (session.availability ?? 0) - reserved);
}
